import {
  useMutation,
  UseQueryResult,
  useQueryClient
} from "@tanstack/react-query";
import {
  Applicant,
  Application,
  RetryIdentityVerificationStatus
} from "types/application";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { ProgressReportView } from "views/applications-id-report/components/ReportsFallback/ProgressReportView";
import {
  ApplicantSettings,
  ApplicantStatus,
  ApplicantType,
  ExecutionModes,
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { VerificationSchema } from "types/landlord/verificationTemplate";
import { TabStepsType } from "views/applications-id-report";
import { instance } from "api/apiBase";
import { logger } from "logger";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";

import { lazy, Suspense } from "react";

const AdditionalDocumentsSlice = lazy(
  () => import("../slices/AdditionalDocumentsSlice")
);
const ApplicantChatSlice = lazy(() => import("../slices/ApplicantChatSlice"));
const BasicInformation = lazy(() => import("../slices/BasicInformation"));
const CreditReportSlice = lazy(
  () => import("views/applications-id-report/slices/CreditReportSlice")
);
const CriminalReportSlice = lazy(() => import("../slices/CriminalReportSlice"));
const DisclosureSlice = lazy(() => import("../slices/DisclosureSlice"));
const EvictionReportSlice = lazy(() => import("../slices/EvictionReportSlice"));
const HousingHistoryReportSlice = lazy(
  () => import("../slices/HousingHistoryReportSlice")
);
const IdentityVerificationReportSlice = lazy(
  () => import("../slices/IdentityVerificationReportSlice")
);
const IncomeReportSlice = lazy(() => import("../slices/IncomeReportSlice"));

import { ManualReportView } from "views/applications-id-report/components/ReportsFallback/ManualReportView";
import { ProcessingReportView } from "views/applications-id-report/components/ReportsFallback/ProcessingReportView";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { UnavailableReportView } from "./ReportsFallback/UnavailableReportView";
import { CanadianCriminalReportSlice } from "views/applications-id-report/slices/CanadianCriminalReportSlice";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";

interface ReportsCardProps {
  applicant?: Applicant;
  currentTab: TabStepsType;
  applicationMeta: UseQueryResult<Application | undefined, unknown>;
  currentVerification?: Verification;
  screeningCriteria?: ApplicantSettings;
  verificationSchemas?: {
    [Key in VerificationNames]: VerificationSchema;
  };
}

export const ReportsCard = ({
  applicant,
  currentTab,
  applicationMeta,
  currentVerification,
  screeningCriteria,
  verificationSchemas
}: ReportsCardProps) => {
  const queryClient = useQueryClient();

  const { mutate: cancelRetryIdentityVerification, isPending } = useMutation({
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${applicationMeta.data?.id}/applicants/${applicant?.id}/cancel_request_retry_identity_verification`
      ),
    onSuccess: () => {
      toast.success("Identity verification retry successfully canceled");
      queryClient
        .refetchQueries({
          queryKey: ["APPLICATION_ID_REPORT", String(applicationMeta.data?.id)]
        })
        .catch((error) => {
          logger.error("Failed to invalidate queries", error);
        });
    },
    onError: (error) => {
      toast.error(
        `Cancel identity verification retry failed: ${getErrorMessage(error)}`
      );
    }
  });

  if (!applicant || !applicationMeta.data) return null;

  if (
    currentVerification?.name === VerificationNames.IdentityVerification &&
    applicant.retry_identity_verification_status ===
      RetryIdentityVerificationStatus.Pending
  )
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className="flex flex-col items-center">
          <ProcessingReportView
            className="!max-w-lg"
            currentVerification={currentVerification}
            applicant={applicant}
          />
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            onClick={() => cancelRetryIdentityVerification()}
            loading={isPending}
            disabled={isPending}
          >
            Cancel retry
          </Button>
        </div>
      </div>
    );

  const isIdentityVerificationOverrideRequired =
    applicationMeta.data.requires_overrides_identity_verification?.applicants.some(
      ({ id }) => id === applicant.id
    );

  if (
    currentVerification?.status === VerificationStatus.Pending &&
    currentVerification.settings?.execution_mode === ExecutionModes.Manual
  )
    return (
      <ManualReportView
        refetch={applicationMeta.refetch}
        applicationStatus={
          applicationMeta.data.applicants.find(
            (appl) => appl.id === applicant.id
          )?.application_status
        }
        applicantId={applicant.id}
        verificationId={currentVerification.id}
        applicationId={applicationMeta.data.id}
      />
    );

  if (
    (currentVerification?.status === VerificationStatus.Pending ||
      currentVerification?.status === VerificationStatus.Skipped) &&
    currentVerification.settings?.execution_mode ===
      ExecutionModes.Conditionally
  )
    return (
      <SkippedReportView
        refetch={applicationMeta.refetch}
        applicantId={applicant.id}
        verificationId={currentVerification.id}
        applicationId={applicationMeta.data.id}
        verificationStatus={currentVerification.status}
      />
    );

  if (
    currentVerification?.status === VerificationStatus.Pending &&
    (currentVerification.is_rerun ||
      applicant.application_status === ApplicantStatus.Submitted)
  )
    return (
      <ProgressReportView
        message={
          currentVerification.is_rerun
            ? "Report re-run in progress. Please wait"
            : undefined
        }
      />
    );

  if (
    isIdentityVerificationOverrideRequired &&
    currentVerification?.name &&
    [
      VerificationNames.CreditReport,
      VerificationNames.CriminalReport,
      VerificationNames.EvictionReport
    ].includes(currentVerification.name)
  )
    return (
      <PendingReportView message="Please review issues with applicant's identity verification report. This report will not run until identity is verified" />
    );

  const isVerifiedIncomeProcessing =
    currentVerification?.name === VerificationNames.IncomeVerification &&
    applicant.verifications.find(
      (ver) => ver.name === VerificationNames.VerifiedIncomeVerification
    )?.status === VerificationStatus.Processing;

  if (
    currentVerification?.name === VerificationNames.IdentityVerification &&
    applicant.retry_identity_verification_status ===
      RetryIdentityVerificationStatus.InProgress
  )
    return (
      <ProcessingReportView
        currentVerification={currentVerification}
        applicant={applicant}
      />
    );
  if (
    currentVerification?.status === VerificationStatus.Processing ||
    isVerifiedIncomeProcessing
  )
    return <ProcessingReportView currentVerification={currentVerification} />;
  if (currentVerification?.status === VerificationStatus.Pending)
    return <PendingReportView />;
  if (currentVerification?.status === VerificationStatus.Canceled)
    return <PendingReportView message="Report was canceled" />;
  if (currentVerification?.status === VerificationStatus.Started)
    return <ProgressReportView />;
  if (currentVerification?.status === VerificationStatus.NoDataAvailable)
    return <UnavailableReportView />;

  // We treat verified and failed as finished
  // if (currentVerification.status === "verified") return <p>Status is verified</p>;

  const verificationId = Number(currentVerification?.id);

  switch (currentTab) {
    case VerificationNames.RentalApplication:
      return (
        <Suspense fallback={null}>
          <BasicInformation
            verificationId={verificationId}
            applicant={applicant}
            applicationMeta={applicationMeta}
            email={applicant.email}
            phone={applicant.phone}
          />
        </Suspense>
      );
    case VerificationNames.CanadianCriminalReport:
      return (
        <Suspense fallback={null}>
          <CanadianCriminalReportSlice
            applicationId={String(applicationMeta.data.id)}
            applicant={applicant}
            isNotResident={
              currentVerification?.status === VerificationStatus.Skipped
            }
          />
        </Suspense>
      );
    case VerificationNames.IdentityVerification:
      return (
        <Suspense fallback={null}>
          <IdentityVerificationReportSlice
            requiresOverridesIdentityVerification={Boolean(
              applicationMeta.data.requires_overrides_identity_verification?.applicants.some(
                ({ id }) => id === applicant.id
              )
            )}
            retryIdentityVerificationStatus={
              applicant.retry_identity_verification_status
            }
            applicationId={String(applicationMeta.data.id)}
            verificationId={verificationId}
            applicant={applicant}
            templateTemplateSettings={
              applicationMeta.data.verification_template?.settings!
            }
          />
        </Suspense>
      );
    case VerificationNames.IncomeVerification:
    case VerificationNames.VerifiedIncomeVerification:
      return (
        <Suspense fallback={null}>
          <IncomeReportSlice
            applicant={applicant}
            screeningCriteria={screeningCriteria}
            verificationTemplate={applicationMeta.data.verification_template}
            templateRecommendationSettings={
              applicationMeta.data.verification_template?.recommendation_method
            }
          />
        </Suspense>
      );
    case VerificationNames.HousingHistory:
      return (
        <Suspense fallback={null}>
          <HousingHistoryReportSlice
            verificationId={verificationId}
            applicant={applicant}
            verificationSchemas={verificationSchemas}
          />
        </Suspense>
      );
    case VerificationNames.CreditReport:
      return (
        <Suspense fallback={null}>
          <CreditReportSlice
            applicant={applicant}
            verificationId={verificationId}
            pastDueAccountType={
              applicationMeta.data.verification_template?.screening_criteria
                ? applicationMeta.data.verification_template.screening_criteria[
                    `${applicant.role}_settings`
                  ]?.past_due_account?.past_due_account_rate
                : ""
            }
            screeningCriteria={screeningCriteria}
          />
        </Suspense>
      );
    case VerificationNames.EvictionReport:
      return (
        <Suspense fallback={null}>
          <EvictionReportSlice
            verificationId={verificationId}
            applicant={applicant}
            screeningCriteria={screeningCriteria}
          />
        </Suspense>
      );
    case VerificationNames.CriminalReport:
      return (
        <Suspense fallback={null}>
          <CriminalReportSlice
            verificationId={verificationId}
            applicant={applicant}
            screeningCriteria={screeningCriteria}
            allowedManualFips={
              applicationMeta.data.verification_template?.settings[
                applicant.role || ApplicantType.Applicant
              ]?.criminal_report?.manual_walkin_county_fips
            }
          />
        </Suspense>
      );
    case "additional_documents":
      return (
        <Suspense fallback={null}>
          <AdditionalDocumentsSlice applicant={applicant} />
        </Suspense>
      );
    case VerificationNames.SubmissionDisclosure:
      return (
        <Suspense fallback={null}>
          <DisclosureSlice
            applicant={applicant}
            verificationId={verificationId}
          />
        </Suspense>
      );
    case "chat":
      return (
        <Suspense fallback={null}>
          <ApplicantChatSlice applicant={applicant} key={applicant.id} />
        </Suspense>
      );
    default:
      return null;
  }
};
